/**
 * espigul/espigul-map — front-end behavior.
 *
 * All server-computed data (translated strings, price ranges, zone/type
 * taxonomy trees, URLs) is passed in as `config`, read from the block
 * wrapper's `data-map-config` JSON attribute (see render.php) rather than
 * being templated directly into this file — this file is enqueued as a
 * plain static script, not run through PHP.
 */
import polygonClipping from 'polygon-clipping';

document.addEventListener('alpine:init', () => {
	Alpine.data('espigulMap', (config) => {
		// map lives as a plain closure variable, not a reactive property on
		// the returned object — Alpine wraps every property of that object
		// in a reactive Proxy, and AdvancedMarkerElement silently fails to
		// render (no error, just nothing painted) when handed a proxied
		// google.maps.Map instance instead of the real one.
		let map = null;
		return {
		operation: config.operation,
		locked: config.locked,
		priceRanges: config.priceRanges,
		listPageUrl: config.listPageUrl,
		markersUrl: config.markersUrl,
		openFilters: false,
		// All houses' Marker objects, created once by createMarkers()
		// and kept off the map (map: null) until visible — keyed by
		// house id so updateMarkers() can toggle them in O(1) instead
		// of constructing/destroying Marker instances on every filter
		// change.
		markersById: {},
		// The subset of markersById currently attached to the map —
		// refreshed by updateMarkers(), the only thing
		// refreshMarkerIcons() needs to touch on a zoom settle.
		visibleMarkers: [],
		infoWindow: null,
		// Set once zones load (see renderZonePolygons()); re-invoked on every
		// zoom settle by onZoomChanged() below.
		zoneStyleFn: null,
		// Every zone term (region/town/neighbourhood), keyed by id — lets
		// the map's click handler walk a clicked polygon's ancestor chain
		// to set all 3 filter levels correctly, regardless of which level
		// was actually clicked.
		zonesById: {},
		bedrooms: [
			{ value: '', label: config.labels.all },
			{ value: 1, label: 1 },
			{ value: 2, label: 2 },
			{ value: 3, label: 3 },
			{ value: 4, label: '4+' }
		],
		zones: config.zones,
		types: config.types,
		filters: {
			zone: config.filters.zone,
			town: config.filters.town,
			neighbourhood: config.filters.neighbourhood,
			type: config.filters.type,
			price_min: '',
			price_max: ''
		},
		filtersReset: {},
		allHouses: [],
		stats: {
			total: 0
		},
		async init() {
			this.filtersReset = JSON.parse(JSON.stringify(this.filters));

			const urlParams = new URLSearchParams(window.location.search);
			for (const [key, value] of urlParams.entries()) {
				if (value == '') continue;
				if (key === 'operation') {
					if (!this.locked && (value === 'sell' || value === 'rent')) {
						this.operation = value;
					}
				} else if (key in this.filters) {
					this.filters[key] = value;
				}
			}

			this.$watch('filters', () => this.syncUrl());
			this.$watch('operation', () => this.syncUrl());

			await this.loadMarkers();
			await this.waitForGoogleMaps();
			await this.initMap();
			this.fetchZones();
			this.createMarkers();
			this.applyFilters();
		},
		waitForGoogleMaps() {
			// google.maps exists as a lightweight stub as soon as the script
			// parses (loaded with &loading=async) — its actual classes (Map,
			// Marker, ...) are only attached once importLibrary() is used, so
			// the real readiness signal is importLibrary being present, not
			// just window.google.maps being truthy.
			return new Promise((resolve) => {
				if (window.google && window.google.maps && window.google.maps.importLibrary) {
					resolve();
					return;
				}
				const interval = setInterval(() => {
					if (window.google && window.google.maps && window.google.maps.importLibrary) {
						clearInterval(interval);
						resolve();
					}
				}, 100);
			});
		},
		async initMap() {
			// Populates google.maps.Map/Marker/InfoWindow/LatLngBounds/etc —
			// importLibrary() both returns and attaches them to the global
			// google.maps namespace, so the rest of the code can keep using
			// `new google.maps.X(...)` as usual afterwards.
			await google.maps.importLibrary('maps');
			await google.maps.importLibrary('marker');

			map = new google.maps.Map(this.$refs.canvas, {
				center: { lat: 41.6, lng: 2.3 },
				zoom: 8,
				// Required for AdvancedMarkerElement to render at all — it
				// silently refuses to display markers on a map with no Map ID.
				mapId: 'a91e7bb793ab46ccc15d658d',
				zoomControl: true,
				maxZoom: 16,
				minZoom: 7,
				mapTypeControl: true,
				mapTypeControlOptions: {
					style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
					mapTypeIds: ['roadmap', 'satellite']
				},
				streetViewControl: false,
				// Disables clicking on the base map's own POI/transit icons
				// (they'd otherwise pop up Google's own info card, competing
				// with our own zone-polygon/marker clicks). Independent of
				// the (deprecated-with-mapId) styles array, so this still
				// works even though styling itself is cloud-based here.
				clickableIcons: false
			});

			// Cap the popup width so the property card can't blow past a
			// phone viewport (which made Google's InfoWindow grow its own
			// inner scrollbar). The rest of the shrinking is CSS — see
			// .gm-style-iw rules in style.scss.
			this.infoWindow = new google.maps.InfoWindow({ maxWidth: 300 });
			// 'idle' only (not 'zoom_changed'): zoom_changed fires on every
			// intermediate frame of an animated zoom (e.g. during
			// fitBounds()'s transition), which — combined with no
			// early-return guard — meant every marker got a brand new SVG
			// icon reconstructed and reassigned on every single frame of
			// that animation. That's very likely what looked like markers
			// flashing/disappearing. 'idle' fires once the map has actually
			// settled, so the swap happens (at most) once per real change.
			map.addListener('idle', () => this.onZoomChanged());
			map.addListener('mapcapabilities_changed', () => {
				const mapCapabilities = map.getMapCapabilities();

				if (!mapCapabilities.isAdvancedMarkersAvailable) {
					// Advanced markers are *not* available, add a fallback.
					console.warn('Advanced markers are not available in this environment. Falling back to legacy markers.');
				} else {
					console.log('Advanced markers are available in this environment.');
				}
			});
			// The map sits in a CSS grid/flex layout with a 100dvh-based
			// height; its container's pixel size can still be settling right
			// after construction — tell Maps to redraw its tiles whenever the
			// container's actual size changes, so tiles/overlays stay aligned
			// with it (independent of the fitBounds() calls in updateMarkers(),
			// which react to filter changes, not layout/resize).
			const resizeObserver = new ResizeObserver(() => {
				google.maps.event.trigger(map, 'resize');
			});
			resizeObserver.observe(this.$refs.canvas);
		},
		// Below this zoom level markers are plain dots (avoids clutter when
		// many pins are visible at once); at/above it they show the price.
		zoomPriceThreshold: 12,
		markersShowPrice: false,
		onZoomChanged() {
			console.log('Zoom changed to', map.getZoom());
			// Zoom only ever affects marker STYLE (dot vs price pill), never
			// WHICH markers are visible — visibility is purely filter-based
			// (see getVisibleHouses()). So this only ever needs to refresh
			// icons, never re-derive/reconcile visibility — which also means
			// it's harmless to fire redundantly (e.g. from our own
			// fitBounds() call in updateMarkers() settling), unlike
			// updateMarkers() would be if it were called from here.
			this.refreshMarkerIcons();
			this.refreshZoneStyles();
		},
		syncUrl() {
			const url = new URL(window.location.href);
			url.search = '';
			for (const key in this.filters) {
				if (this.filters[key] != '') {
					url.searchParams.set(key, this.filters[key]);
				}
			}
			if (!this.locked) {
				url.searchParams.set('operation', this.operation);
			}
			history.pushState(null, document.title, url.toString());
		},
		listUrl() {
			if (!this.listPageUrl) return '#';
			const params = new URLSearchParams();
			for (const key in this.filters) {
				if (this.filters[key] != '') {
					params.set(key, this.filters[key]);
				}
			}
			if (!this.locked) {
				params.set('operation', this.operation);
			}
			const query = params.toString();
			return query ? this.listPageUrl + '?' + query : this.listPageUrl;
		},
		setOperation(op) {
			if (this.locked || this.operation === op) return;
			this.operation = op;
			this.filters.price_min = '';
			this.filters.price_max = '';
			this.filter();
		},
		setFilter(filter, value) {
			this.filters[filter] = value;
			// A town belongs to whichever zone was previously selected, and
			// a neighbourhood to whichever town — carrying either over when
			// switching a broader level would silently filter against an
			// unrelated slug.
			if (filter === 'zone') {
				this.filters.town = '';
				this.filters.neighbourhood = '';
			} else if (filter === 'town') {
				this.filters.neighbourhood = '';
			}
			this.filter();
		},
		filter() {
			this.applyFilters();
		},
		reset() {
			this.filters = JSON.parse(JSON.stringify(this.filtersReset));
			this.applyFilters();
			// applyFilters() -> updateMarkers() just fit bounds to the
			// (now empty, no filters active) visible set, which no-ops —
			// so the map would otherwise stay wherever it happened to be.
			// Reframe to every house instead, for a proper "start over"
			// view rather than a fixed hardcoded center/zoom.
			this.fitBoundsToHouses(this.allHouses);
		},
		getTowns() {
			if (this.filters.zone == '') {
				return [];
			}
			const currentZone = this.zones.find(zone => zone.slug == this.filters.zone);
			return currentZone ? currentZone.children : [];
		},
		getNeighbourhoods() {
			if (this.filters.town == '') {
				return [];
			}
			const currentTown = this.getTowns().find(town => town.slug == this.filters.town);
			return currentTown ? currentTown.children : [];
		},
		checkMinPrice(val) {
			return this.filters.price_min > val;
		},
		async loadMarkers() {
			try {
				const response = await fetch(this.markersUrl, {
					headers: { 'Accept': 'application/json' }
				});
				this.allHouses = await response.json();
			} catch (error) {
				console.log(error);
				this.allHouses = [];
			}
		},
		// Builds every house's AdvancedMarkerElement once, kept off the map
		// (map: null) until updateMarkers() decides it should be visible.
		// Reusing these via the .map property toggle instead of constructing/
		// destroying a marker (with a fresh click listener) on every filter
		// change avoids that churn — e.g. switching zone A -> zone B -> back
		// to A used to rebuild zone A's markers from scratch each time.
		// Called once from init(), after loadMarkers() and before the first
		// applyFilters(). Uses AdvancedMarkerElement (not the legacy Marker)
		// because legacy Marker's default batched-canvas rendering silently
		// fails to repaint setIcon() calls once enough markers are on screen
		// at once — AdvancedMarkerElement is a real DOM node per marker, so
		// content swaps always repaint.
		createMarkers() {
			this.allHouses.forEach((house) => {
				const marker = new google.maps.marker.AdvancedMarkerElement({
					map: map,
					position: { lat: house.lat, lng: house.lng },
					title: house.title,
					content: this.getMarkerIcon(house)
				});
				marker.houseData = house;

				marker.addListener('gmp-click', () => {
					this.infoWindow.setContent(this.buildCardHtml(house));
					this.infoWindow.open(map, marker);
				});

				this.markersById[house.id] = marker;
			});
		},
		// Pure function of current filter state -> which houses should be
		// visible right now. No side effects, no map/marker mutation — the
		// single definition of "what's showing" that updateMarkers() below
		// (the only place allowed to touch markers) reconciles the map
		// against, from every trigger (filters, operation, AND zoom).
		getVisibleHouses() {
			// No markers until AT LEAST ONE filter is active (zone, town,
			// type, bedrooms, or a price bound) — showing every property
			// across the whole map at once isn't useful at that zoomed-out
			// scale. Any single filter is enough though: e.g. picking a
			// bedroom count with no zone selected should still narrow the
			// map down to matching houses everywhere, not require a zone
			// first.
			const hasActiveFilter = this.filters.zone || this.filters.town || this.filters.neighbourhood
				|| this.filters.type || this.filters.bedrooms || this.filters.price_min || this.filters.price_max;
			if (!hasActiveFilter) return [];

			const priceKey = this.operation === 'sell' ? 'priceSell' : 'priceRent';

			console.log('Filtering houses for operation', this.operation, 'with price key', priceKey);

			console.log(this.filters, this.allHouses)

			return this.allHouses.filter((house) => {
				const price = house[priceKey];
				// A price of exactly 0 means this house isn't actually for
				// this operation (not just "unset") — exclude it the same
				// as null/undefined.
				if (!price) return false;
				if (this.filters.zone && !house.zones.includes(this.filters.zone)) return false;
				if (this.filters.town && !house.zones.includes(this.filters.town)) return false;
				if (this.filters.neighbourhood && !house.zones.includes(this.filters.neighbourhood)) return false;
				if (this.filters.type && house.type !== this.filters.type) return false;
				if (this.filters.bedrooms && house.bedrooms < this.filters.bedrooms) return false;
				if (this.filters.price_min && price < this.filters.price_min) return false;
				if (this.filters.price_max && price > this.filters.price_max) return false;
				return true;
			});
		},
		// The single place that shows/hides markers (via setMap(), against
		// the pool createMarkers() built). Called after every filter/
		// operation change AND after every zoom settle (via onZoomChanged)
		// — always reconciling the map's actual markers against
		// getVisibleHouses() + the current zoom, rather than filter changes
		// and zoom changes each partially mutating marker state through
		// separate, only loosely coordinated paths (which is what let a
		// previous zone's markers linger/reappear).
		updateMarkers() {
			const visibleHouses = this.getVisibleHouses();
			console.log('Visible houses after filtering', visibleHouses.length, 'of', this.allHouses.length);
			this.stats.total = visibleHouses.length;
			this.fitBoundsToHouses(visibleHouses);

			const visibleIds = new Set(visibleHouses.map((house) => house.id));
			console.log('Updating markers', visibleIds.size);

			this.visibleMarkers = [];
			Object.values(this.markersById).forEach((marker) => {
				const visible = visibleIds.has(marker.houseData.id);
				// AdvancedMarkerElement exposes .map as a plain property, not a
				// setMap() method like the legacy Marker.
				marker.map = visible ? map : null;
				if (visible) this.visibleMarkers.push(marker);
			});

			// Refresh every currently-visible marker's icon so it always
			// matches the current zoom, regardless of whether it just became
			// visible or already was.
			this.refreshMarkerIcons();
		},
		// Marker STYLE only (dot vs price pill) — never touches which
		// markers are visible. Safe to call as often as needed (zoom
		// changes, including ones we trigger ourselves) since it can never
		// make a marker appear/disappear. Only iterates visibleMarkers, not
		// the full pool, so cost stays proportional to what's on screen.
		refreshMarkerIcons() {
			this.markersShowPrice = map.getZoom() >= this.zoomPriceThreshold;
			console.log('Refreshing marker icons, zoom', map.getZoom(), 'showPrice?', this.markersShowPrice);
			// AdvancedMarkerElement swaps its rendered content via the .content
			// property (a real DOM node) rather than legacy Marker's setIcon().
			Object.values(this.markersById).forEach((marker) => {
				marker.content = this.getMarkerIcon(marker.houseData);
			});
		},
		// Reframes the map around a set of houses. Called from
		// updateMarkers() with whatever's currently visible — recomputed
		// from the actual visible houses every time (not a zone's own
		// precomputed polygon extent), so it reacts correctly to ANY
		// filter change, not only which zone is selected — and from
		// reset() with every house, for a "start over" view.
		fitBoundsToHouses(houses) {
			if (!houses.length) return;

			const bounds = new google.maps.LatLngBounds();
			houses.forEach((house) => bounds.extend({ lat: house.lat, lng: house.lng }));
			map.fitBounds(bounds);
		},
		// All filtering happens in-memory over the already-loaded marker
		// list — no network round-trip, so this stays instant even with
		// hundreds/thousands of properties.
		applyFilters() {
			console.log('Apply filters!');
			this.updateMarkers();
			this.refreshZoneStyles();
		},
		// Re-invokes zoneStyleFn — needed whenever something the style
		// function reads changes without the features' own properties
		// changing (zoom, or which zone/town is selected), since
		// Data.setStyle() only auto-reruns on a feature property change.
		// Guarded since fetchZones() isn't awaited in init(), so this can
		// run before zones have loaded.
		refreshZoneStyles() {
			if (this.zoneStyleFn) {
				map.data.setStyle(this.zoneStyleFn);
			}
		},
		getMarkerIcon(house) {
			return this.markersShowPrice ? this.buildMarkerIcon(this.getPriceLabel(house)) : this.buildDotIcon();
		},
		buildDotIcon() {
			const size = 16;
			const el = document.createElement('div');
			el.style.width = `${size}px`;
			el.style.height = `${size}px`;
			el.style.boxSizing = 'border-box';
			el.style.borderRadius = '50%';
			el.style.background = '#000000';
			el.style.border = '2px solid #ffffff';
			// AdvancedMarkerElement anchors plain content at its bottom-center
			// by default (like a pin tip) — shift down by half the element's
			// own height so the visual CENTER lands on the coordinate instead,
			// matching the legacy Icon's center anchor.
			el.style.transform = 'translateY(50%)';
			return el;
		},
		// Plain-text price label for the marker's SVG icon.
		getPriceLabel(house) {
			const price = this.operation === 'sell' ? house.priceSell : house.priceRent;
			if (price === null || price === undefined) return '';
			const formatted = new Intl.NumberFormat('es-ES', {
				style: 'currency',
				currency: 'EUR',
				maximumFractionDigits: 0
			}).format(price);
			return this.operation === 'rent' ? `${formatted}/mes` : formatted;
		},
		// Builds a black rounded-pill marker icon with the price as its label,
		// sized to fit the text (measured via an offscreen canvas).
		buildMarkerIcon(label) {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			const fontSize = 12;
			ctx.font = `bold ${fontSize}px sans-serif`;
			const textWidth = ctx.measureText(label).width;

			const paddingX = 10;
			const height = 24;
			const width = Math.ceil(textWidth) + paddingX * 2;
			const radius = height / 2;

			const el = document.createElement('div');
			el.style.width = `${width}px`;
			el.style.height = `${height}px`;
			el.style.boxSizing = 'border-box';
			el.style.borderRadius = `${radius}px`;
			el.style.background = '#000000';
			el.style.color = '#ffffff';
			el.style.fontFamily = 'sans-serif';
			el.style.fontSize = `${fontSize}px`;
			el.style.fontWeight = 'bold';
			el.style.display = 'flex';
			el.style.alignItems = 'center';
			el.style.justifyContent = 'center';
			// See buildDotIcon() — same bottom-center -> center anchor fix.
			el.style.transform = 'translateY(50%)';
			el.textContent = label;
			return el;
		},
		formatPrice(house) {
			const price = this.operation === 'sell' ? house.priceSell : house.priceRent;
			if (!price) return '';
			const formatted = new Intl.NumberFormat('es-ES', {
				style: 'currency',
				currency: 'EUR',
				maximumFractionDigits: 0
			}).format(price);
			return this.operation === 'rent' ? `${formatted} / <small>${config.labels.month}</small>` : formatted;
		},
		// Builds the marker-click InfoWindow card using the exact same
		// classes as template-parts/property/item.php (espigul-house__item
		// and its BEM children) so it picks up the site's real property-card
		// styles (loaded theme-wide, see assets/styles/components/_house.css)
		// instead of a bespoke lookalike — all client-side from data already
		// in map-markers.json, no per-click network request. Simplified vs
		// the PHP version in one place: a single thumbnail instead of the
		// full multi-image carousel, since only one image is in that JSON.
		buildCardHtml(house) {
			const alsoPrice = this.operation === 'sell' ? house.priceRent : house.priceSell;
			const alsoLabel = this.operation === 'sell' ? config.labels.rentOption : config.labels.sellOption;
			const attributes = (house.attributes || []).map((attribute) => `
				<div class="espigul-house__item__body__meta__item">
					<span class="espigul-house__item__body__meta__item__label">${attribute.label}</span>
					<span class="espigul-house__item__body__meta__item__value">${attribute.value}</span>
				</div>
			`).join('');

			return `
				<div class="espigul-house__item espigul-house__item--map-popup cursor-pointer" onclick="window.open('${house.link}', '_blank', 'noopener')">
					<div class="espigul-house__item__gallery espigul-house__item__photo">
						${alsoPrice ? `<div class="espigul-house__item__photo__also">${alsoLabel}</div>` : ''}
						<ul class="espigul-house__item__gallery__items">
							<li class="espigul-house__item__gallery__items__item espigul-house__item__gallery__items__item--active">
								<img src="${house.image || ''}" alt="${house.title}" loading="lazy" width="100%" height="auto">
							</li>
						</ul>
					</div>
					<div class="espigul-house__item__body">
						<div class="espigul-house__item__body__city">${house.town} | ${house.year}</div>
						<h2 class="espigul-house__item__body__title flex-grow">
							<a href="${house.link}" target="_blank" rel="noopener">${house.title}</a>
						</h2>
						<div class="espigul-house__item__body__price">${this.formatPrice(house)}</div>
						<div class="espigul-house__item__body__meta">${attributes}</div>
					</div>
				</div>
			`;
		},
		fetchZones() {
			// depth=3 loads regions + towns + neighbourhoods in one call;
			// cache_only=1 means this can NEVER trigger a live Nominatim
			// lookup — populating deeper levels is the WP-CLI backfill's job
			// (`wp espigul fetch-zone-boundaries --parent=0 --depth=3
			// --force`), not something a page view should ever do live.
			fetch(`${config.wpUrl}/wp-json/espigul/zones?parent=0&depth=3&cache_only=1`, {
				method: 'get',
				headers: {
					'Accept': 'application/json'
				}
			}).then((response) => response.json()).then((zones) => {
				this.renderZonePolygons(zones);
			}).catch((error) => {
				console.log(error);
			});
		},
		// Which zone level should be visible at a given zoom.
		levelForZoom(zoom) {
			if (zoom < 12) return 0;
			if (zoom < 14) return 1;
			return 2;
		},
		// zone_kind="aggregate" boundaries (see zone-boundaries.php) are just
		// each direct child's polygon concatenated into one MultiPolygon —
		// touching towns still render as separate parts with a visible
		// internal seam. Dissolve shared edges here so the map shows one
		// contiguous shape. Safe no-op for a MultiPolygon whose parts don't
		// actually touch (union of disjoint polygons returns them
		// unmerged), and for a plain Polygon (returned as-is).
		dissolveBoundary(boundary) {
			if (boundary.type !== 'MultiPolygon') {
				return boundary;
			}

			try {
				const merged = polygonClipping.union(...boundary.coordinates.map((polygon) => [polygon]));
				return { type: 'MultiPolygon', coordinates: merged };
			} catch (error) {
				console.log('Zone boundary union failed, rendering raw (unmerged) geometry', error);
				return boundary;
			}
		},
		renderZonePolygons(zones) {
			// Keyed by id (not just the renderable/property-having subset)
			// so the click handler below can always resolve a clicked
			// polygon's full ancestor chain, even through a 0-property
			// intermediate level.
			zones.forEach((zone) => {
				this.zonesById[zone.id] = zone;
			});

			// A zone with 0 properties isn't useful to show or click
			// through to (selecting one would just yield an empty result)
			// — excluded before anything else, so it's also correctly
			// invisible to the allChildrenCovered fallback below (a
			// zero-property neighbourhood shouldn't block its parent city
			// from ever "graduating" to hidden).
			const zonesWithProperties = zones.filter((zone) => zone.count > 0);

			// Only real areas are drawable as a clickable region — a bare
			// Point (e.g. stale/legacy cached data) would otherwise render as
			// a plain default marker via the Data layer, which looks like
			// unexplained stray map data.
			const renderable = zonesWithProperties.filter(
				(zone) => zone.boundary && (zone.boundary.type === 'Polygon' || zone.boundary.type === 'MultiPolygon')
			);

			// Zoom-independent: does EVERY child of this term have its own
			// renderable polygon one level down? Computed once here, reused
			// unchanged on every zoom change. Deliberately ALL, not ANY — a
			// partially-covered set of children (e.g. only 2 of Sant
			// Cugat's 5 neighbourhoods have a fetched polygon) would
			// otherwise hide the parent's polygon too, leaving a visible
			// gap where neither the parent nor the missing children show
			// anything. The per-branch fallback only ever needs to look one
			// level down, never further (a level-0 term hides as soon as
			// ALL its level-1 children are covered, regardless of whether
			// those in turn have level-2 children or not).
			const totalChildrenByParent = new Map();
			zonesWithProperties.forEach((zone) => {
				totalChildrenByParent.set(zone.parent, (totalChildrenByParent.get(zone.parent) || 0) + 1);
			});
			const renderableChildrenByParent = new Map();
			renderable.forEach((zone) => {
				renderableChildrenByParent.set(zone.parent, (renderableChildrenByParent.get(zone.parent) || 0) + 1);
			});
			const allChildrenCovered = (zoneId) => {
				const total = totalChildrenByParent.get(zoneId) || 0;
				return total > 0 && renderableChildrenByParent.get(zoneId) === total;
			};

			renderable.forEach((zone) => {
				const features = map.data.addGeoJson({
					type: 'Feature',
					properties: {
						slug: zone.slug,
						name: zone.name,
						level: zone.level,
						id: zone.id,
						allChildrenCovered: allChildrenCovered(zone.id)
					},
					geometry: this.dissolveBoundary(zone.boundary)
				});

			});

			// A style FUNCTION, not a static object: visibility depends on
			// the current zoom, recomputed per-feature. Note this does NOT
			// auto-rerun when zoom changes (Data.setStyle() only re-invokes
			// the function when a feature's own properties change) — see
			// onZoomChanged(), which explicitly re-calls setStyle() with this
			// same function reference on every zoom settle.
			this.zoneStyleFn = (feature) => {
				const target = this.levelForZoom(map.getZoom());
				const level = feature.getProperty('level');
				const visible = level === target || (level < target && !feature.getProperty('allChildrenCovered'));
				// The actively-selected zone/town/neighbourhood is
				// highlighted in the brand color — every other polygon
				// keeps the default black outline/fill. Only the single
				// most specific selection is ever compared against, so
				// e.g. selecting a neighbourhood highlights just that
				// neighbourhood, not its parent town/zone too.
				const selected = feature.getProperty('slug') === (this.filters.neighbourhood || this.filters.town || this.filters.zone);
				const color = selected ? '#4F3F3B' : '#000000';

				return {
					fillColor: color,
					fillOpacity: selected ? 0.25 : 0.08,
					strokeColor: color,
					strokeWeight: 1,
					// Paired explicitly rather than relying solely on
					// visible:false to suppress hit-testing.
					clickable: visible,
					visible: visible
				};
			};
			map.data.setStyle(this.zoneStyleFn);

			// Hover highlight — overrideStyle()/revertStyle() only touch the
			// one feature under the cursor, rather than re-invoking
			// zoneStyleFn (and re-deriving visibility/selection) for every
			// feature on the map like setStyle() would.
			map.data.addListener('mouseover', (event) => {
				map.data.overrideStyle(event.feature, {
					fillColor: '#4F3F3B',
					fillOpacity: 0.25,
					strokeColor: '#4F3F3B'
				});
			});
			map.data.addListener('mouseout', (event) => {
				map.data.revertStyle(event.feature);
			});

			map.data.addListener('click', (event) => {
				const slug = event.feature.getProperty('slug');
				const id = event.feature.getProperty('id');
				if (!slug) return;

				// setFilter('zone', slug) always assumed the clicked
				// polygon was a top-level zone — wrong as soon as towns/
				// neighbourhoods got their own polygons, since clicking one
				// of those set filters.zone to a town/neighbourhood slug
				// instead of filling in filters.town/neighbourhood, leaving
				// the aside dropdowns out of sync with the map. Walk the
				// clicked feature's actual ancestor chain instead, and set
				// all 3 levels directly from it.
				const slugsByLevel = ['', '', ''];
				let zone = this.zonesById[id];
				while (zone) {
					slugsByLevel[zone.level] = zone.slug;
					zone = this.zonesById[zone.parent];
				}

				this.filters.zone = slugsByLevel[0];
				this.filters.town = slugsByLevel[1];
				this.filters.neighbourhood = slugsByLevel[2];

				// applyFilters() -> updateMarkers() already reconciles the
				// map to the new selection's markers and fits the viewport
				// to them. A second, separate fitBounds() here (to the
				// clicked polygon's own — usually much wider — shape) used
				// to fire right after, competing with the marker-based one
				// and the zoom-cap listener tied to it; that janky double
				// pan/zoom transition is what looked like the old
				// selection's markers lingering.
				this.filter();
			});
		}
		};
	});
});
